import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@nubank/nuds-web/components/Box/Box';
import Image from '@nubank/nuds-web/components/Image/Image';
import styled from 'styled-components';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Icon from '@nubank/nuds-web/components/Icon/Icon';
import { createPortal } from 'react-dom';

import QuestionModal from '../QuestionModal/QuestionModal';

function removeAccentsAndSpecialChars(str) {
  const accentsMap = {
    á: 'a',
    é: 'e',
    í: 'i',
    ó: 'o',
    ú: 'u',
    à: 'a',
    è: 'e',
    ì: 'i',
    ò: 'o',
    ù: 'u',
    â: 'a',
    ê: 'e',
    î: 'i',
    ô: 'o',
    û: 'u',
    ä: 'a',
    ë: 'e',
    ï: 'i',
    ö: 'o',
    ü: 'u',
    ã: 'a',
    õ: 'o',
    ñ: 'n',
    ç: 'c',
  };

  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, c => accentsMap[c] || '')
    .replace(/[^\w\s]|_/g, '')
    .toLowerCase();
}

const connectorWords = ['el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'y', 'o', 'con', 'sin', 'por', 'para', 'mi', 'mis', 'de', 'del'];

function removeConnectorWords(query) {
  const words = query.split(' ');

  const filteredWords = words.filter(word => !connectorWords.includes(word.toLowerCase()));

  return filteredWords.join(' ');
}

const StyledInput = styled.input`
  font-family: Graphik-Medium, Graphik-Regular,'Gotham SSm A','Gotham SSm B','Helvetica Neue',Helvetica,Arial,sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.9px;
  width: auto;
  padding-right: 0;

  /* stylelint-disable selector-no-vendor-prefix */
  ::-webkit-input-placeholder {
    color: #00000052;
  }

  @media (width >= 768px) {
    font-size: 64px;
    font-style: normal;
    font-weight: 500;
    line-height: 100%;
    letter-spacing: -1.92px;
    padding-right: 240px;
  }
  `;

const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: translateY(0);
  background-color: #0009;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  overflow: hidden;
  transition: top 0.3s ease;

  & button:nth-child(2) {
    display: none;
  }

  @media (width >= 768px) {
    max-height: none;
    align-items: flex-start;

    & button {
      display: block;
    }
  }
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  height:  100%;
  max-height: 100vh;
  overflow-y: hidden;
  width: 100%;

  @media (width >= 768px) {
    max-height: 65vh;
  }
`;

const ResultsSection = styled.div`
  height: 100%;
  overflow-y: auto;
  margin-top: 64px;

  &::-webkit-scrollbar {
    width: 0.4em;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const ResultItem = styled(Box)`
  height: 106px;
  cursor: pointer;

  &:hover {
    p {
      color: #820AD1;
    }
  }
`;

const StyledResultList = styled(Box)`
  gap: 50px 31px;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
`;

const Closebutton = styled(Box)`
  cursor: pointer;
`;

const StyledIcon = styled(Icon)`
  margin-left: 8px;
  
  path {
    fill: #820AD1;
    stroke: #820AD1;
  }
`;

function SearchQuestionModal({ closeModal }) {
  const { formatMessage } = React.useContext(NuDsContext);
  const allQuestions = formatMessage({ id: 'cms.allQuestions' });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState({
    title: '',
    answer: '',
  });

  const inputRef = React.createRef();

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // Adjust the debounce delay as needed (e.g., 300ms)

    return () => clearTimeout(debounceTimeout);
  }, [searchQuery]);

  const handleSearch = query => {
    // Remove connector words from the search query
    const cleanedQuery = removeConnectorWords(query);

    // Perform the search using cleanedQuery
    const results = allQuestions.filter(({ question }) => {
      const normalizedQuestion = removeAccentsAndSpecialChars(question);
      const normalizedSearchQuery = removeAccentsAndSpecialChars(cleanedQuery);
      return removeConnectorWords(normalizedQuestion).includes(normalizedSearchQuery);
    });

    return results;
  };

  const filteredQuestions = debouncedSearchQuery.length >= 3
    ? handleSearch(debouncedSearchQuery)
    : allQuestions;

  const handleQuestionClick = questionId => {
    const selected = filteredQuestions.find(question => question.id === questionId);
    setSelectedQuestion(selected);
    setIsOpen(true);
  };

  const closeQuestionModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <StyledModal onClick={closeModal}>
        <ModalContent onClick={e => e.stopPropagation()}>

          {/* CLOSE BUTTON */}

          <Closebutton
            id="close-button"
            onClick={closeModal}
            display="flex"
            justifyContent="flex-end"
            marginTop={{ xs: '14px', lg: '64px' }}
            marginRight={{ xs: '32px', lg: '240px' }}
          >
            <Image
              alt="Cerrar"
              src="benefits/close-big.svg"
              webp={false}
              width="auto"
              importance="high"
            />
          </Closebutton>

          {/* SEARCH INPUT */}

          <Box
            id="search-box"
            marginTop={{ xs: '52px', lg: '0' }}
            marginLeft={{ xs: '32px', md: '32px', lg: '240px' }}
            paddingRight={{ xs: '32px', md: '32px', lg: '240px' }}
          >
            <StyledInput
              placeholder="Escribe tu pregunta aquí"
              type="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              ref={inputRef}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </Box>

          {/* QUESTIONS RESULT LIST */}

          <ResultsSection>
            {searchQuery.length >= 3 && (
            <StyledResultList
              id="questions-result-list"
              display="grid"
              paddingBottom={{ xs: '400px', lg: '30%' }}
              marginRight={{ xs: '32px', lg: '240px' }}
              marginLeft={{ xs: '32px', lg: '240px' }}
            >
              {filteredQuestions.map(question => (

                <ResultItem
                  key={question.id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  height="106px"
                  maxHeight="106px"
                  width="100%"
                  paddingVertical="29px"
                  paddingLeft={{ xs: '16px', lg: '29px' }}
                  paddingRight={{ xs: '36px', lg: '29px' }}
                  borderRadius="12px"
                  backgroundColor="#F8F8FA"
                  justifySelf="center"
                  maxWidth="600px"
                  onClick={() => handleQuestionClick(question.id)}
                >

                  <Typography
                    strong
                    variant="subtitle2"
                    width="250px"
                    marginRight="8px"
                    color="black.default"
                  >
                    {question.question}
                  </Typography>

                  <StyledIcon name="arrow-right" color="primary" />

                </ResultItem>

              ))}
            </StyledResultList>
            )}
          </ResultsSection>

        </ModalContent>
      </StyledModal>

      {isOpen && typeof document !== 'undefined' && allQuestions && createPortal(
        <QuestionModal
          closeModal={closeQuestionModal}
          title={selectedQuestion.question}
          answer={selectedQuestion.answer}
        />, document.getElementById('modal-root'),
      )}
    </>
  );
}

SearchQuestionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default SearchQuestionModal;
